import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import {
  LayoutDashboard, Users, BookOpen, UserCog, ShieldCheck, RefreshCw,
  CheckCircle, AlertTriangle, XCircle, Info, ChevronDown, ChevronRight,
  Activity, BookMarked, GraduationCap, Globe, Award, FileText,
  BarChart3, ClipboardList, Calendar, Settings, Video, Gamepad2,
  PenTool, Languages, Eye, Loader2, Sparkles, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  healthy: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle, label: 'Healthy' },
  warning: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, label: 'Warning' },
  urgent:  { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Urgent Action Needed' },
  critical:{ color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Critical' },
  info:    { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', icon: Info, label: 'Info' },
};

const sidebarItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/quality', label: 'Quality Assurance', icon: ShieldCheck },
  { path: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/enrollments', label: 'Enrollments', icon: ClipboardList },
  { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
  { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
];

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.info;
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} ${cfg.border} border`}>
      <Icon className="w-3.5 h-3.5" /> {cfg.label}
    </span>
  );
};

const MetricCard = ({ label, value, sub, icon: Icon, status }) => {
  const border = status === 'critical' ? 'border-red-200' : status === 'warning' ? 'border-amber-200' : 'border-slate-200';
  return (
    <div className={`bg-white rounded-xl border ${border} p-4`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
};

const HealthRow = ({ check }) => {
  const cfg = STATUS_CONFIG[check.status] || STATUS_CONFIG.info;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <Icon className={`w-4 h-4 ${cfg.color}`} />
        <span className="text-sm font-medium text-slate-700">{check.name}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{check.detail}</span>
        <StatusBadge status={check.status} />
      </div>
    </div>
  );
};

const IssueTable = ({ issues, title, severity, onFix }) => {
  const [expanded, setExpanded] = useState(false);
  if (!issues || issues.length === 0) return null;
  const cfg = STATUS_CONFIG[severity] || STATUS_CONFIG.warning;
  const shown = expanded ? issues : issues.slice(0, 5);

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4 mb-4`}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center justify-between w-full text-left">
        <h4 className={`font-semibold text-sm ${cfg.color} flex items-center gap-2`}>
          {React.createElement(cfg.icon, { className: "w-4 h-4" })}
          {title} ({issues.length})
        </h4>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      <div className="mt-3 space-y-1.5">
        {shown.map((issue, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className={`font-mono px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color} shrink-0`}>
              {issue.key_stage || ''} {issue.subject || ''}
            </span>
            <span className="text-slate-700 flex-1">{issue.detail}</span>
            {issue.topic_name && <span className="text-slate-400 shrink-0">({issue.topic_name})</span>}
            {issue.type === 'low_question_count' && onFix && (
              <button
                data-testid={`fix-btn-${issue.topic_id}`}
                onClick={(e) => { e.stopPropagation(); onFix(issue); }}
                className="px-2 py-0.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors font-medium shrink-0 flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> Fix
              </button>
            )}
          </div>
        ))}
      </div>
      {issues.length > 5 && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-slate-500 hover:text-slate-700 mt-2 underline">
          {expanded ? 'Show less' : `Show all ${issues.length} issues`}
        </button>
      )}
    </div>
  );
};

const CoverageBar = ({ label, value, total }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const color = pct === 100 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-slate-600 mb-1">
        <span>{label}</span>
        <span className="font-semibold">{value}/{total} ({pct}%)</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const QualityDashboard = () => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [fixingTopic, setFixingTopic] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [fixAllProgress, setFixAllProgress] = useState(null);

  const runScan = useCallback(async (showToast = false) => {
    try {
      setScanning(true);
      const [scanRes, covRes] = await Promise.all([
        api.get('/quality/scan'),
        api.get('/quality/content-coverage'),
      ]);
      setData(scanRes.data);
      setCoverage(covRes.data);
      if (showToast) toast.success('QA scan complete');
    } catch (err) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { runScan(); }, [runScan]);

  const handleFixQuestions = async (issue) => {
    const key = issue.topic_id;
    setFixingTopic(key);
    try {
      const res = await api.post('/quality/fix-questions', {
        topic_id: issue.topic_id,
        topic_name: issue.topic_name || issue.topic_id,
        key_stage: issue.key_stage || '',
        subject: issue.subject || '',
        target_count: 5,
      });
      toast.success(`Generated ${res.data.generated} questions for ${issue.topic_name || issue.topic_id}`);
      runScan(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setFixingTopic(null);
    }
  };

  const handleFixAll = async () => {
    setFixingAll(true);
    setFixAllProgress({ completed: 0, total: 0, current_topic: 'Starting...' });
    try {
      const res = await api.post('/quality/fix-all-questions');
      const jobId = res.data.job_id;
      const total = res.data.total;

      if (res.data.already_running) {
        toast.info(res.data.message);
      }

      if (!jobId) {
        toast.success(res.data.message);
        setFixingAll(false);
        setFixAllProgress(null);
        return;
      }

      setFixAllProgress({ completed: 0, total, current_topic: 'Processing...' });

      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const status = await api.get(`/quality/fix-all-status/${jobId}`);
          const job = status.data;
          setFixAllProgress({
            completed: job.completed,
            total: job.total,
            fixed: job.fixed,
            failed: job.failed,
            current_topic: job.current_topic,
            status: job.status
          });

          if (job.status === 'complete' || job.status === 'stopped') {
            clearInterval(poll);
            if (job.status === 'complete') {
              toast.success(`Fixed ${job.fixed}/${job.total} topics`);
            } else {
              toast.warning(`Job stopped at ${job.completed}/${job.total}. ${job.fixed} topics fixed. You can re-run to continue.`);
            }
            setFixingAll(false);
            setFixAllProgress(null);
            runScan(false);
          }
        } catch {
          // Continue polling
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk fix failed to start');
      setFixingAll(false);
      setFixAllProgress(null);
    }
  };

  if (loading) {
    return (
      <ProLayout title="Quality Assurance" sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          <span className="ml-3 text-slate-600">Running initial scan...</span>
        </div>
      </ProLayout>
    );
  }

  const s = data?.summary || {};
  const iss = data?.issues || {};
  const eng = data?.engagement || {};
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'content', label: 'Content Coverage' },
    { id: 'health', label: 'System Health' },
    { id: 'engagement', label: 'Engagement' },
    { id: 'issues', label: `Issues (${iss.total || 0})` },
  ];

  return (
    <ProLayout title="Quality Assurance" sidebarItems={sidebarItems}>
      <div data-testid="quality-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <StatusBadge status={data?.overall_status || 'healthy'} />
            <span className="text-xs text-slate-400">
              Last scan: {data?.scan_timestamp ? new Date(data.scan_timestamp).toLocaleString() : 'Never'}
            </span>
          </div>
          <Button
            data-testid="rescan-btn"
            onClick={() => runScan(true)}
            disabled={scanning}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning...' : 'Re-scan Now'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Top Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Curriculum Topics" value={s.curriculum?.total_topics || 0} sub={`${s.curriculum?.question_coverage || 0}% have questions`} icon={BookMarked} />
              <MetricCard label="ESOL Topics" value={s.esol?.total_topics || 0} sub={`${s.esol?.coverage || 0}% with content`} icon={Globe} />
              <MetricCard label="Exam Papers" value={(s.exams?.sats_papers || 0) + (s.exams?.gcse_papers || 0)} sub={`${s.exams?.sats_papers || 0} SATs, ${s.exams?.gcse_papers || 0} GCSE`} icon={Award} />
              <MetricCard label="11+ Programme" value={s.eleven_plus?.topics || 0} sub={`${s.eleven_plus?.practice_papers || 0} practice papers`} icon={GraduationCap} />
            </div>

            {/* Quick Health + Issues */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Activity className="w-4 h-4" /> System Health</h3>
                {data?.health_checks?.map((c, i) => <HealthRow key={i} check={c} />)}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Issue Summary</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-sm text-red-700 font-medium">Critical</span>
                    <span className="text-xl font-bold text-red-700">{iss.critical_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="text-sm text-amber-700 font-medium">Warnings</span>
                    <span className="text-xl font-bold text-amber-700">{iss.warning_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm text-blue-700 font-medium">Info</span>
                    <span className="text-xl font-bold text-blue-700">{iss.info_count || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Engagement Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Students" value={eng.total_students || 0} sub={`${eng.active_students || 0} active`} icon={Users} />
              <MetricCard label="Lesson Completion" value={`${eng.lesson_completion_rate || 0}%`} sub={`${eng.lessons_completed || 0} / ${eng.lessons_assigned || 0}`} icon={BookOpen} />
              <MetricCard label="Avg Quiz Score" value={`${eng.avg_quiz_score || 0}%`} sub={`${eng.total_quiz_attempts || 0} attempts`} icon={BarChart3} />
              <MetricCard label="Games Played" value={eng.game_completions || 0} icon={Gamepad2} />
              <MetricCard label="Exams Taken" value={eng.exam_submissions || 0} icon={PenTool} />
            </div>
          </div>
        )}

        {/* CONTENT COVERAGE TAB */}
        {activeTab === 'content' && coverage && (
          <div className="space-y-6">
            {Object.entries(coverage).map(([ks, subjects]) => (
              <div key={ks} className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4">{ks}</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {Object.entries(subjects).map(([subj, info]) => (
                    <div key={subj}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize text-slate-700">{subj}</span>
                        <StatusBadge status={info.missing === 0 ? 'healthy' : 'warning'} />
                      </div>
                      <CoverageBar label="Complete" value={info.complete} total={info.total} />
                      <CoverageBar label="Partial" value={info.partial} total={info.total} />
                      {info.missing > 0 && <CoverageBar label="Missing" value={info.missing} total={info.total} />}
                      <div className="mt-3 space-y-1">
                        {info.topics?.map(t => (
                          <div key={t.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                            <span className="text-slate-600 truncate mr-2">{t.name}</span>
                            <div className="flex gap-2 shrink-0">
                              <span className={`px-1.5 py-0.5 rounded ${t.questions >= 5 ? 'bg-emerald-50 text-emerald-700' : t.questions > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                                {t.questions}Q
                              </span>
                              <span className={`px-1.5 py-0.5 rounded ${t.lessons >= 3 ? 'bg-emerald-50 text-emerald-700' : t.lessons > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                                {t.lessons}L
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SYSTEM HEALTH TAB */}
        {activeTab === 'health' && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-800 mb-4">System Health Checks</h3>
            {data?.health_checks?.map((c, i) => <HealthRow key={i} check={c} />)}
          </div>
        )}

        {/* ENGAGEMENT TAB */}
        {activeTab === 'engagement' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Total Students" value={eng.total_students || 0} icon={Users} />
              <MetricCard label="Active Students" value={eng.active_students || 0} icon={Activity} />
              <MetricCard label="ESOL Progress" value={eng.esol_progress_records || 0} icon={Globe} />
              <MetricCard label="Essay Submissions" value={eng.essay_submissions || 0} icon={Languages} />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">Lesson Progress</h3>
                <div className="text-center py-4">
                  <p className="text-4xl font-bold text-emerald-700">{eng.lesson_completion_rate || 0}%</p>
                  <p className="text-sm text-slate-500 mt-1">Completion Rate</p>
                  <p className="text-xs text-slate-400 mt-1">{eng.lessons_completed || 0} completed / {eng.lessons_assigned || 0} assigned</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-3">Quiz Performance</h3>
                <div className="text-center py-4">
                  <p className="text-4xl font-bold text-blue-700">{eng.avg_quiz_score || 0}%</p>
                  <p className="text-sm text-slate-500 mt-1">Average Score</p>
                  <p className="text-xs text-slate-400 mt-1">{eng.total_quiz_attempts || 0} total attempts</p>
                </div>
              </div>
            </div>

            {/* Low pass rate topics */}
            {eng.low_pass_rate_topics && eng.low_pass_rate_topics.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Topics with Low Pass Rates
                </h3>
                <div className="space-y-2">
                  {eng.low_pass_rate_topics.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2">
                      <span className="font-mono text-red-700">{t.topic_id}</span>
                      <div className="flex gap-4 text-xs">
                        <span className="text-red-600">Avg: {t.avg_score}%</span>
                        <span className="text-slate-500">{t.attempts} attempts</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ISSUES TAB */}
        {activeTab === 'issues' && (
          <div className="space-y-4">
            {/* Fix All Button */}
            {iss.warning_count > 0 && (
              <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
                <div>
                  <p className="font-semibold text-slate-800">Bulk Fix — Low Question Counts</p>
                  <p className="text-xs text-slate-500">Generate AI questions for all topics that need more question sets</p>
                </div>
                <Button
                  data-testid="fix-all-btn"
                  onClick={handleFixAll}
                  disabled={fixingAll}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  {fixingAll ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Fixing all topics...</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Fix All Topics</>
                  )}
                </Button>
              </div>
            )}

            {(fixingTopic || fixingAll) && (
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
                  <span className="text-sm text-emerald-700 font-medium">
                    {fixingAll && fixAllProgress
                      ? `Fixing topics: ${fixAllProgress.completed || 0}/${fixAllProgress.total || 0} — ${fixAllProgress.current_topic || ''}`
                      : `Generating questions for ${fixingTopic}...`}
                  </span>
                </div>
                {fixingAll && fixAllProgress && fixAllProgress.total > 0 && (
                  <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${Math.round((fixAllProgress.completed / fixAllProgress.total) * 100)}%` }}
                    />
                  </div>
                )}
                {fixingAll && fixAllProgress && fixAllProgress.fixed > 0 && (
                  <p className="text-xs text-emerald-600">{fixAllProgress.fixed} fixed, {fixAllProgress.failed || 0} failed</p>
                )}
              </div>
            )}
            {iss.critical_count > 0 && (
              <IssueTable issues={iss.critical} title="Critical Issues" severity="critical" onFix={handleFixQuestions} />
            )}
            {iss.warning_count > 0 && (
              <IssueTable issues={iss.warnings} title="Warnings" severity="warning" onFix={handleFixQuestions} />
            )}
            {iss.info_count > 0 && (
              <IssueTable issues={iss.info} title="Informational" severity="info" />
            )}
            {(iss.total || 0) === 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-emerald-700 font-semibold text-lg">All Clear</p>
                <p className="text-emerald-600 text-sm">No issues found across the platform.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default QualityDashboard;
